import { AuthenticationManager } from '@frontend/authentication-v2';
import { BasketCheckoutClient, BasketClient, BasketItemClient } from '@frontend/basket/api';
import { BasketStatus } from '@frontend/basket/types';
import { AllowanceClient } from '@frontend/budget/api';
import { CertificationClient } from '@frontend/certificate/api';
import { UserCertification } from '@frontend/certificate/types';
import { ProductClient } from '@frontend/product/api';
import { ProductType } from '@frontend/product/types';

import { fetchShopPages } from './shop-catalog';
import { ShopDraft, ShopScope } from './shop.types';

export type ShopCheckoutError = 'eligibility' | 'checkout';

export class ShopEligibilityError extends Error {}

export async function submitShopBasket(scope: ShopScope, draft: ShopDraft, saveProgress: (basketId: string, checkoutPending: boolean) => void): Promise<void> {
    if (!scope.orderWorkflowId) throw new Error('Shop order workflow is not configured');
    const token = await AuthenticationManager.getInstance().getTokenWhenReady();
    if (!token || token.entity_id !== scope.userId || token.entity_type !== 'user') throw new Error('Shop identity changed');
    if (!draft.spotId || !draft.items.length || (scope.enforceCostCenterUse && !draft.costCenterId)) throw new ShopEligibilityError();

    let basket = draft.basketId ? await BasketClient.fetchBasket(scope.accountId, draft.basketId) : null;
    if (basket && (basket.user_id !== scope.userId || basket.account_id !== scope.accountId)) throw new Error('Basket owner changed');
    // A checkout response may have been lost. Do not submit the same order again.
    if (basket?.status === BasketStatus.CHECKED_OUT) return;
    if (basket) {
        if (basket.cost_center_id !== draft.costCenterId) await BasketClient.patchBasket(scope.accountId, basket.id, { cost_center_id: draft.costCenterId });
        saveProgress(basket.id, false);
    }

    const ids = draft.items.map((item) => item.product.id);
    const products = (await ProductClient.resolveProducts(ids)).results;
    const equipmentIds = products.filter((product) => product.type === ProductType.EQUIPMENT).map((product) => product.id);
    const [allowances, certifications] = await Promise.all([
        AllowanceClient.postGetUserAllowances(draft.spotId, ids, token),
        equipmentIds.length
            ? CertificationClient.postGetProductCertification(scope.accountId, scope.userId, equipmentIds, token)
            : Promise.resolve<UserCertification>({})
    ]);
    for (const item of draft.items) {
        const product = products.find((candidate) => candidate.id === item.product.id);
        const allowance = allowances[item.product.id];
        if (
            !product ||
            product.delete_timestamp ||
            !Number.isSafeInteger(item.quantity) ||
            item.quantity < 1 ||
            !allowance?.allowed ||
            allowance.quantity < item.quantity ||
            (product.type === ProductType.EQUIPMENT && !certifications[product.id]?.certified)
        ) {
            throw new ShopEligibilityError();
        }
    }

    if (!basket) {
        basket = await BasketClient.postBasket(scope.accountId, { status: BasketStatus.OPEN, cost_center_id: draft.costCenterId }, token);
        saveProgress(basket.id, false);
    }
    const basketId = basket.id;
    const remoteItems = await fetchShopPages((page) => BasketItemClient.fetchBasketItems({ ...page, basket_id: basketId }));
    const remainingProducts = new Set(ids);
    // Reconcile by product rather than replaying POSTs after a partial failure.
    for (const remote of remoteItems) {
        const local = draft.items.find((item) => item.product.id === remote.product_id);
        if (!local || !remainingProducts.has(remote.product_id)) {
            await BasketItemClient.deleteBasketItem(scope.accountId, basketId, remote.id);
        } else {
            await BasketItemClient.patchBasketItem(scope.accountId, basketId, remote.id, { quantity: local.quantity });
            remainingProducts.delete(remote.product_id);
        }
    }
    for (const item of draft.items) {
        if (remainingProducts.has(item.product.id)) {
            await BasketItemClient.postBasketItem(scope.accountId, basketId, { product_id: item.product.id, quantity: item.quantity }, token);
        }
    }
    saveProgress(basketId, true);
    await BasketCheckoutClient.checkoutBasket(
        scope.accountId,
        basketId,
        { spot_id: draft.spotId, ...(scope.orderWorkflowId ? { order_workflow_id: scope.orderWorkflowId } : {}) },
        token
    );
}
